import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import APIService from 'services/APIService'
import ComplianceSetupExplainer from 'features/compliance/ComplianceSetupExplainer'

const api = APIService()

const ComplianceSetupProgress = ({ bpId }) => {
  const navigate = useNavigate()
  const [setup, setSetup] = useState(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!bpId) return
    setDismissed(localStorage.getItem(`complianceSetupDoneDismissed:${bpId}`) === '1')
    let cancelled = false
    api.get(`/building-passport/${bpId}/compliance/summary`)
      .then(res => { if (!cancelled) setSetup(res?.data?.data?.setup || null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [bpId])

  if (!setup) return null
  const total = setup.total || 0
  const complete = setup.complete || 0
  const outstanding = setup.outstanding ?? Math.max(0, total - complete)
  if (total === 0) return null

  if (outstanding === 0) {
    if (dismissed) return null
    return (
      <Wrap $done>
        <Head>
          <IconChip $done><CheckCircleRoundedIcon style={{ fontSize: 16 }} /></IconChip>
          <HeadText>
            <LabelRow>
              <Label>Compliance setup</Label>
              <ComplianceSetupExplainer align='left' />
            </LabelRow>
            <Title>Setup complete. All {total} obligations reviewed</Title>
            <Sub>
              Every obligation is confirmed as applicable or marked not applicable. Open the
              obligation list any time to change a decision.
            </Sub>
          </HeadText>
          <ContinueBtn onClick={() => { localStorage.setItem(`complianceSetupDoneDismissed:${bpId}`, '1'); setDismissed(true) }}>
            Dismiss
          </ContinueBtn>
        </Head>
      </Wrap>
    )
  }

  const pct = total > 0 ? Math.round((complete / total) * 100) : 0

  return (
    <Wrap>
      <Head>
        <IconChip><ChecklistRoundedIcon style={{ fontSize: 16 }} /></IconChip>
        <HeadText>
          <LabelRow>
            <Label>Compliance setup</Label>
            <ComplianceSetupExplainer align='left' />
          </LabelRow>
          <Title>
            {outstanding} of {total} obligations still to review
          </Title>
          <Sub>
            “Review” means confirming an obligation applies to this Building Passport, or marking
            it not applicable. Separate from whether it’s satisfied.
            {setup.skipped > 0 ? ` ${setup.skipped} marked not applicable so far.` : ''}
          </Sub>
        </HeadText>
        <ContinueBtn onClick={() => navigate(`/bp-list/${bpId}/programme`)}>
          Continue setup <ArrowForwardRoundedIcon style={{ fontSize: 15 }} />
        </ContinueBtn>
      </Head>

      <Track>
        <Fill style={{ width: `${Math.max(2, pct)}%` }} />
      </Track>
    </Wrap>
  )
}

export default ComplianceSetupProgress

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 12px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #ececec;
  border-left: 3px solid ${({ $done }) => $done ? '#15803d' : '#d97706'};
  border-radius: 14px;
`

const Head = styled.div`
  display: flex; align-items: center; gap: 12px;
`

const IconChip = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: 9px;
  background: ${({ $done }) => $done ? '#e3f4e9' : '#fbece1'};
  color: ${({ $done }) => $done ? '#15803d' : '#d97706'};
  flex-shrink: 0;
`

const HeadText = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 3px;
`

const LabelRow = styled.div`
  display: flex; align-items: center; gap: 8px;
`

const Label = styled.span`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const Title = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  font-variant-numeric: tabular-nums;
`

const Sub = styled.span`
  font-size: 11.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ContinueBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  border: none; border-radius: 999px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { filter: brightness(1.08); }
`

const Track = styled.div`
  height: 6px;
  background: #f4f4f6;
  border-radius: 999px;
  overflow: hidden;
`

const Fill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #15803d;
  transition: width 400ms cubic-bezier(0.22, 0.61, 0.36, 1);
`
